"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Image from "next/image";
import { Check, ChevronDown, Search, X } from "lucide-react";
import {
  searchCompanyNames,
  type CompanyKind,
  type CompanyNameSuggestion,
} from "../actions/searchCompanyNames";

/**
 * Lets the panel-level "Expand all / Collapse all" toggle drive every <details>
 * without threading props through each <Section>. `epoch` bumps on each toggle
 * so the <details> remounts with the new `open` value; between toggles the prop
 * is stable, so individual section toggles persist (React skips unchanged DOM).
 */
const SectionOpenContext = createContext<{ epoch: number; allOpen: boolean }>({
  epoch: 0,
  allOpen: false,
});
import type { TopSellersFilterState, TopSellersFilters } from "../types";
import { toggleInArray } from "../utils";
import {
  CONTENT_DESCRIPTOR_LABELS,
  type ContentDescriptorId,
} from "@/features/games/types";

const STEAM_CDN_BASE = "https://store.fastly.steamstatic.com/";

// Cap on checkbox rows rendered per filter section — the full tag list is
// thousands of entries; the search input narrows anything beyond the cap.
const MAX_FILTER_OPTIONS = 250;

function CheckboxRow({
  checked,
  label,
  onClick,
  icon,
}: {
  checked: boolean;
  label: string;
  onClick: () => void;
  icon?: string | null;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="w-full flex items-center gap-2 py-1 text-left hover:text-white transition-colors group"
    >
      <span
        className={`flex-shrink-0 w-3.5 h-3.5 rounded-sm flex items-center justify-center transition-colors ${
          checked
            ? "bg-[#66c0f4]"
            : "bg-transparent border border-[#4a5568] group-hover:border-[#66c0f4]"
        }`}
      >
        {checked && <Check className="w-2.5 h-2.5 text-[#1b2838] stroke-[3]" />}
      </span>
      {icon && (
        <Image
          src={`${STEAM_CDN_BASE}${icon}`}
          alt=""
          width={24}
          height={24}
          sizes="24px"
          className="flex-shrink-0 object-contain"
          priority={false}
        />
      )}
      <span className={`text-[13px] truncate ${checked ? "text-white" : "text-[#8f98a0]"}`}>
        {label}
      </span>
    </button>
  );
}

function Section({
  title,
  badge,
  children,
  collapsible = true,
}: {
  title: string;
  badge?: number;
  children: React.ReactNode;
  collapsible?: boolean;
}) {
  const { epoch, allOpen } = useContext(SectionOpenContext);
  if (!collapsible) {
    return (
      <div className="py-2">
        <div className="flex items-center gap-2 min-w-0 pb-2">
          <span className="text-[13px] font-medium text-[#c7d5e0] truncate uppercase tracking-wide">
            {title}
          </span>
          {badge != null && badge > 0 && (
            <span className="text-[11px] text-[#66c0f4] flex-shrink-0">({badge})</span>
          )}
        </div>
        <div className="pl-5 pb-3 pt-1">{children}</div>
      </div>
    );
  }

  return (
    <details key={epoch} className="group" open={allOpen}>
      <summary className="list-none cursor-pointer py-2 flex items-center justify-between gap-2 hover:text-white transition-colors select-none">
        <div className="flex items-center gap-2 min-w-0">
          <ChevronDown className="w-3.5 h-3.5 text-[#66c0f4] transition-transform group-open:rotate-180" />
          <span className="text-[13px] font-medium text-[#c7d5e0] group-hover:text-white truncate uppercase tracking-wide">
            {title}
          </span>
          {badge != null && badge > 0 && (
            <span className="text-[11px] text-[#66c0f4] flex-shrink-0">({badge})</span>
          )}
        </div>
      </summary>
      <div className="pl-5 pb-3 pt-1">{children}</div>
    </details>
  );
}

function SearchInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="mb-2 relative">
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#6b7785]" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search…"
        className="h-7 w-full bg-[#316282]/30 pl-7 pr-2 text-[13px] text-white placeholder-[#6b7785] focus:outline-none focus:bg-[#316282]/50 transition-colors"
      />
    </div>
  );
}

function Slider({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  symbol = "≥",
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  symbol?: string;
}) {
  const currentValue = value ?? min;
  const percentage = ((currentValue - min) / (max - min)) * 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[13px] text-white">
          {label}: {symbol}
          {currentValue}%
        </span>
      </div>
      <div className="relative">
        <input
          type="range"
          aria-label={`${label} ${symbol}${currentValue}%`}
          min={min}
          max={max}
          value={currentValue}
          onChange={(e) => {
            const newValue = Number(e.target.value);
            onChange(newValue === min ? null : newValue);
          }}
          className="w-full h-1.5 bg-[#316282]/30 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_2px_rgba(0,0,0,0.3)] [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:h-3.5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:shadow-[0_0_2px_rgba(0,0,0,0.3)]"
          style={{
            background: `linear-gradient(to right, #66c0f4 0%, #66c0f4 ${percentage}%, rgba(49, 98, 130, 0.19) ${percentage}%, rgba(49, 98, 130, 0.19) 100%)`,
          }}
        />
      </div>
    </div>
  );
}

const NUM_INPUT_CLS =
  "h-8 w-20 bg-[#316282]/30 px-2.5 text-[13px] text-white placeholder-[#6b7785] focus:outline-none focus:bg-[#316282]/50 transition-colors";

/** Integer Min/Max pair — mirrors the Price inputs (reviews/followers/CCU are counts). */
function MinMaxInputs({
  from,
  to,
  onFrom,
  onTo,
  ariaLabel,
}: {
  from: number | null;
  to: number | null;
  onFrom: (n: number | null) => void;
  onTo: (n: number | null) => void;
  ariaLabel: string;
}) {
  const handle =
    (setter: (n: number | null) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === "") return setter(null);
      if (!/^\d+$/.test(raw)) return;
      const n = Number(raw);
      if (Number.isFinite(n)) setter(n);
    };
  return (
    <div className="flex items-center gap-2">
      <input
        value={from == null ? "" : String(from)}
        onChange={handle(onFrom)}
        inputMode="numeric"
        aria-label={`${ariaLabel} min`}
        className={NUM_INPUT_CLS}
        placeholder="Min"
      />
      <span className="text-[#6b7785] text-xs">—</span>
      <input
        value={to == null ? "" : String(to)}
        onChange={handle(onTo)}
        inputMode="numeric"
        aria-label={`${ariaLabel} max`}
        className={NUM_INPUT_CLS}
        placeholder="Max"
      />
    </div>
  );
}

/**
 * Publisher/Developer picker: debounced autocomplete against
 * analytics.app_company_names (via the searchCompanyNames server action) +
 * removable chips for the selected exact names. Exact names matter — the RPC
 * matches with `publishers && p_publishers` (case-sensitive array overlap).
 */
function CompanyFilterInput({
  kind,
  selected,
  onChange,
}: {
  kind: CompanyKind;
  selected: string[] | null;
  onChange: (next: string[] | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<CompanyNameSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const reqId = ++reqIdRef.current;
    const t = setTimeout(async () => {
      const res = await searchCompanyNames(kind, q);
      if (reqIdRef.current !== reqId) return; // stale response
      setSuggestions(res);
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [query, kind]);

  const current = selected ?? [];

  const add = (name: string) => {
    if (!current.includes(name)) onChange([...current, name]);
    setQuery("");
    setSuggestions([]);
  };

  const remove = (name: string) => {
    const next = current.filter((x) => x !== name);
    onChange(next.length ? next : null);
  };

  const visible = suggestions.filter((s) => !current.includes(s.name));

  return (
    <div className="space-y-2">
      {current.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {current.map((name) => (
            <span
              key={name}
              className="inline-flex max-w-full items-center gap-1 rounded-sm bg-[#316282]/40 px-1.5 py-0.5 text-[12px] text-white"
            >
              <span className="truncate">{name}</span>
              <button
                type="button"
                aria-label={`Remove ${name}`}
                onClick={() => remove(name)}
                className="shrink-0 text-[#8f98a0] transition-colors hover:text-white"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#6b7785]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${kind}s…`}
          className="h-7 w-full bg-[#316282]/30 pl-7 pr-2 text-[13px] text-white placeholder-[#6b7785] focus:outline-none focus:bg-[#316282]/50 transition-colors"
        />
      </div>
      {loading && (
        <p className="text-[11px] text-[#6b7785]">Searching…</p>
      )}
      {!loading && visible.length > 0 && (
        <div className="win-scrollbar max-h-40 space-y-0.5 overflow-y-auto overscroll-contain">
          {visible.map((s) => (
            <button
              key={s.name}
              type="button"
              onClick={() => add(s.name)}
              className="flex w-full items-center justify-between gap-2 py-1 text-left transition-colors hover:text-white"
            >
              <span className="truncate text-[13px] text-[#8f98a0]">{s.name}</span>
              <span className="shrink-0 text-[11px] text-[#6b7785]">
                {s.appCount}
              </span>
            </button>
          ))}
        </div>
      )}
      {!loading && query.trim().length >= 2 && visible.length === 0 && (
        <p className="text-[11px] text-[#6b7785]">Nothing found</p>
      )}
    </div>
  );
}

export type FilterExcludeSection = "comingSoon" | "releaseDate";

interface TopSellersFiltersPanelProps {
  filters: TopSellersFilters;
  value: TopSellersFilterState;
  onChange: (next: TopSellersFilterState) => void;
  /** Секції, які не показувати (напр. для monthly — дата і coming_soon вже задані) */
  excludeSections?: FilterExcludeSection[];
  /**
   * Controlled "expand all" state. When `onToggleAll` is provided the panel
   * hides its built-in toggle and uses these instead — lets a parent (e.g. the
   * /search sidebar) render the Expand-all button in its own "Filters" header.
   * Omit all three for the default self-managed toggle (drawers / rank lists).
   */
  allOpen?: boolean;
  openEpoch?: number;
  onToggleAll?: () => void;
}

export function TopSellersFiltersPanel({
  filters,
  value,
  onChange,
  excludeSections = [],
  allOpen,
  openEpoch,
  onToggleAll,
}: TopSellersFiltersPanelProps) {
  const hideComingSoon = excludeSections.includes("comingSoon");
  const hideReleaseDate = excludeSections.includes("releaseDate");
  const [genreQuery, setGenreQuery] = useState("");
  const [categoryQuery, setCategoryQuery] = useState("");
  const [languageQuery, setLanguageQuery] = useState("");
  const [tagQuery, setTagQuery] = useState("");

  const sortedFilters = useMemo(() => ({
    genres: [...(filters.genres ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    categories: [...(filters.categories ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    tags: [...(filters.tags ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    languages: [...(filters.languages ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
  }), [filters]);

  const selectedCounts = useMemo(() => ({
    genres: value.genres?.length ?? 0,
    categories: value.categories?.length ?? 0,
    languages: value.languages?.length ?? 0,
    tags: value.tags?.length ?? 0,
  }), [value]);

  const filteredGenres = useMemo(() => {
    const q = genreQuery.trim().toLowerCase();
    return q ? sortedFilters.genres.filter((g) => g.name.toLowerCase().includes(q)) : sortedFilters.genres;
  }, [genreQuery, sortedFilters.genres]);

  const filteredCategories = useMemo(() => {
    const q = categoryQuery.trim().toLowerCase();
    return q ? sortedFilters.categories.filter((c) => c.name.toLowerCase().includes(q)) : sortedFilters.categories;
  }, [categoryQuery, sortedFilters.categories]);

  const filteredTags = useMemo(() => {
    const q = tagQuery.trim().toLowerCase();
    return q ? sortedFilters.tags.filter((t) => t.name.toLowerCase().includes(q)) : sortedFilters.tags;
  }, [tagQuery, sortedFilters.tags]);

  const filteredLanguages = useMemo(() => {
    const q = languageQuery.trim().toLowerCase();
    return q ? sortedFilters.languages.filter((l) => l.name.toLowerCase().includes(q)) : sortedFilters.languages;
  }, [languageQuery, sortedFilters.languages]);

  // "Expand all / Collapse all" — drives every <Section> via context. Either
  // self-managed (built-in toggle) or controlled by the parent via props.
  const [internalAllOpen, setInternalAllOpen] = useState(false);
  const [internalEpoch, setInternalEpoch] = useState(0);
  const controlled = onToggleAll != null;
  const effectiveAllOpen = controlled ? !!allOpen : internalAllOpen;
  const effectiveEpoch = controlled ? openEpoch ?? 0 : internalEpoch;
  const toggleAllSections = controlled
    ? onToggleAll
    : () => {
        setInternalAllOpen((v) => !v);
        setInternalEpoch((e) => e + 1);
      };

  return (
    <SectionOpenContext.Provider
      value={{ epoch: effectiveEpoch, allOpen: effectiveAllOpen }}
    >
      {!controlled && (
        <div className="flex items-center justify-end pb-1">
          <button
            type="button"
            onClick={toggleAllSections}
            className="text-[11px] font-medium text-[#8f98a0] transition-colors hover:text-white"
          >
            {effectiveAllOpen ? "Collapse all" : "Expand all"}
          </button>
        </div>
      )}
      <div className="divide-y divide-[#2a3f5f]/30">
      <Section title="Type" badge={value.appTypes?.length ?? 0}>
        <div className="space-y-0.5">
          {/* "All types" — checked when no specific type is selected. Clicking
              it clears the array (deselects everything). */}
          <CheckboxRow
            checked={!value.appTypes?.length}
            label="All types"
            onClick={() => onChange({ ...value, appTypes: null })}
          />
          {/* Individual types — multi-select. Toggling re-syncs the array;
              array becomes null when empty so "All types" lights up again. */}
          {[
            { v: "game", label: "Games" },
            { v: "dlc", label: "DLCs" },
            { v: "demo", label: "Demos" },
            { v: "music", label: "Music" },
            { v: "hardware", label: "Hardware" },
          ].map((opt) => {
            const current = value.appTypes ?? [];
            const checked = current.includes(opt.v);
            return (
              <CheckboxRow
                key={opt.v}
                checked={checked}
                label={opt.label}
                onClick={() => {
                  const next = checked
                    ? current.filter((x) => x !== opt.v)
                    : [...current, opt.v];
                  onChange({
                    ...value,
                    appTypes: next.length ? next : null,
                  });
                }}
              />
            );
          })}
        </div>
      </Section>

      <Section title="Platform" badge={value.os?.length ?? 0}>
        <p className="text-[11px] text-[#8f98a0] mb-2">
          Must support every selected OS
        </p>
        <div className="space-y-0.5">
          {[
            { v: "windows", label: "Windows" },
            { v: "mac", label: "macOS" },
            { v: "linux", label: "Linux / Steam Deck" },
          ].map((opt) => (
            <CheckboxRow
              key={opt.v}
              checked={(value.os ?? []).includes(opt.v)}
              label={opt.label}
              onClick={() =>
                onChange({ ...value, os: toggleInArray(value.os, opt.v) })
              }
            />
          ))}
        </div>
      </Section>

      {/* Pages that own the release window (monthly/upcoming/prerelease pass
          excludeSections=["comingSoon"]) get a REDUCED Early-Access-only
          section — released/coming-soon options are meaningless there, but
          EA-only / no-EA still narrows within the page's own window. Same
          releaseStatus field + ?status= URL param. */}
      {hideComingSoon && (
        <Section
          title="Early Access"
          badge={Number(
            value.releaseStatus === "early_access" ||
              value.releaseStatus === "full_release",
          )}
        >
          <div className="space-y-0.5">
            {(
              [
                { v: null, label: "Any" },
                { v: "early_access", label: "Early Access only" },
                { v: "full_release", label: "No Early Access" },
              ] as const
            ).map((opt) => (
              <CheckboxRow
                key={opt.label}
                checked={
                  opt.v === null
                    ? value.releaseStatus !== "early_access" &&
                      value.releaseStatus !== "full_release"
                    : value.releaseStatus === opt.v
                }
                label={opt.label}
                onClick={() =>
                  onChange({ ...value, releaseStatus: opt.v, upcomingOnly: false })
                }
              />
            ))}
          </div>
        </Section>
      )}

      {!hideComingSoon && (
        <Section
          title="Release status"
          badge={
            Number(value.releaseStatus != null || value.upcomingOnly) +
            Number(value.eaExitFrom != null) +
            Number(value.eaExitTo != null)
          }
        >
          <div className="space-y-0.5">
            {/* Single-select: picking one clears the others (and the legacy
                upcomingOnly flag, which "Coming soon" replaces). */}
            {(
              [
                { v: null, label: "Any" },
                { v: "released", label: "Released (all)" },
                { v: "full_release", label: "Full release — no Early Access" },
                { v: "early_access", label: "Early Access only" },
                { v: "coming_soon", label: "Coming soon" },
              ] as const
            ).map((opt) => (
              <CheckboxRow
                key={opt.label}
                checked={
                  opt.v === null
                    ? value.releaseStatus == null && !value.upcomingOnly
                    : value.releaseStatus === opt.v
                }
                label={opt.label}
                onClick={() =>
                  onChange({
                    ...value,
                    releaseStatus: opt.v,
                    upcomingOnly: false,
                  })
                }
              />
            ))}
          </div>
          <div className="mt-3">
            <p className="text-[11px] text-[#8f98a0] mb-2">
              Leaves Early Access between (scheduled)
            </p>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2">
                <span className="w-9 shrink-0 text-[11px] text-[#8f98a0]">From</span>
                <input
                  type="date"
                  aria-label="EA exit from"
                  value={value.eaExitFrom ?? ""}
                  onChange={(e) =>
                    onChange({ ...value, eaExitFrom: e.target.value || null })
                  }
                  className="h-8 min-w-0 flex-1 bg-[#316282]/30 px-2 text-[13px] text-white focus:outline-none focus:bg-[#316282]/50 transition-colors [color-scheme:dark]"
                />
              </label>
              <label className="flex items-center gap-2">
                <span className="w-9 shrink-0 text-[11px] text-[#8f98a0]">To</span>
                <input
                  type="date"
                  aria-label="EA exit to"
                  value={value.eaExitTo ?? ""}
                  onChange={(e) =>
                    onChange({ ...value, eaExitTo: e.target.value || null })
                  }
                  className="h-8 min-w-0 flex-1 bg-[#316282]/30 px-2 text-[13px] text-white focus:outline-none focus:bg-[#316282]/50 transition-colors [color-scheme:dark]"
                />
              </label>
            </div>
          </div>
        </Section>
      )}

      <Section title="Price" badge={Number(value.priceFrom != null) + Number(value.priceTo != null)}>
        <div className="space-y-2">
          <CheckboxRow
            checked={value.priceFrom === 0 && value.priceTo === 0}
            label="Free only"
            onClick={() => {
              const isFree = value.priceFrom === 0 && value.priceTo === 0;
              onChange(
                isFree
                  ? { ...value, priceFrom: null, priceTo: null }
                  : { ...value, priceFrom: 0, priceTo: 0 },
              );
            }}
          />
          <div className="flex items-center gap-2">
          <input
            value={value.priceFrom == null ? "" : String(value.priceFrom)}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") return onChange({ ...value, priceFrom: null });
              if (!/^\d+(\.\d+)?$/.test(raw)) return;
              const n = Number(raw);
              if (Number.isFinite(n)) onChange({ ...value, priceFrom: n });
            }}
            inputMode="numeric"
            className="h-8 w-20 bg-[#316282]/30 px-2.5 text-[13px] text-white placeholder-[#6b7785] focus:outline-none focus:bg-[#316282]/50 transition-colors"
            placeholder="Min"
          />
          <span className="text-[#6b7785] text-xs">—</span>
          <input
            value={value.priceTo == null ? "" : String(value.priceTo)}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") return onChange({ ...value, priceTo: null });
              if (!/^\d+(\.\d+)?$/.test(raw)) return;
              const n = Number(raw);
              if (Number.isFinite(n)) onChange({ ...value, priceTo: n });
            }}
            inputMode="numeric"
            className="h-8 w-20 bg-[#316282]/30 px-2.5 text-[13px] text-white placeholder-[#6b7785] focus:outline-none focus:bg-[#316282]/50 transition-colors"
            placeholder="Max"
          />
          </div>
        </div>
      </Section>

      <Section title="Rating" badge={Number(value.ratingFrom != null)}>
        <Slider
          label="Rating"
          value={value.ratingFrom}
          onChange={(val) => onChange({ ...value, ratingFrom: val, ratingTo: null })}
          min={0}
          max={100}
          symbol="≥"
        />
      </Section>

      <Section
        title="Reviews"
        badge={Number(value.reviewsFrom != null) + Number(value.reviewsTo != null)}
      >
        <p className="text-[11px] text-[#8f98a0] mb-2">Number of reviews</p>
        <MinMaxInputs
          from={value.reviewsFrom}
          to={value.reviewsTo}
          onFrom={(n) => onChange({ ...value, reviewsFrom: n })}
          onTo={(n) => onChange({ ...value, reviewsTo: n })}
          ariaLabel="Reviews"
        />
      </Section>

      <Section title="Discount" badge={Number(value.discountFrom != null)}>
        <Slider
          label="Discount"
          value={value.discountFrom}
          onChange={(val) => onChange({ ...value, discountFrom: val, discountTo: null })}
          min={0}
          max={100}
          symbol=">"
        />
      </Section>

      <Section
        title="Players right now"
        badge={Number(value.ccuFrom != null) + Number(value.ccuTo != null)}
      >
        <p className="text-[11px] text-[#8f98a0] mb-2">
          Concurrent players at the last hourly sample
        </p>
        <MinMaxInputs
          from={value.ccuFrom}
          to={value.ccuTo}
          onFrom={(n) => onChange({ ...value, ccuFrom: n })}
          onTo={(n) => onChange({ ...value, ccuTo: n })}
          ariaLabel="Players right now"
        />
      </Section>

      <Section
        title="Peak players"
        badge={Number(value.peakCcuFrom != null) + Number(value.peakCcuTo != null)}
      >
        <p className="text-[11px] text-[#8f98a0] mb-2">All-time peak concurrent players</p>
        <MinMaxInputs
          from={value.peakCcuFrom}
          to={value.peakCcuTo}
          onFrom={(n) => onChange({ ...value, peakCcuFrom: n })}
          onTo={(n) => onChange({ ...value, peakCcuTo: n })}
          ariaLabel="Peak players"
        />
      </Section>

      <Section
        title="Followers"
        badge={
          Number(value.followersFrom != null) + Number(value.followersTo != null)
        }
      >
        <p className="text-[11px] text-[#8f98a0] mb-2">Steam community hub followers</p>
        <MinMaxInputs
          from={value.followersFrom}
          to={value.followersTo}
          onFrom={(n) => onChange({ ...value, followersFrom: n })}
          onTo={(n) => onChange({ ...value, followersTo: n })}
          ariaLabel="Followers"
        />
      </Section>

      <Section
        title="Achievements"
        badge={
          Number(value.achievementsFrom != null) +
          Number(value.achievementsTo != null)
        }
      >
        <div className="space-y-2">
          <CheckboxRow
            checked={value.achievementsFrom === 1 && value.achievementsTo == null}
            label="Has achievements"
            onClick={() => {
              const isOn =
                value.achievementsFrom === 1 && value.achievementsTo == null;
              onChange(
                isOn
                  ? { ...value, achievementsFrom: null, achievementsTo: null }
                  : { ...value, achievementsFrom: 1, achievementsTo: null },
              );
            }}
          />
          <MinMaxInputs
            from={value.achievementsFrom}
            to={value.achievementsTo}
            onFrom={(n) => onChange({ ...value, achievementsFrom: n })}
            onTo={(n) => onChange({ ...value, achievementsTo: n })}
            ariaLabel="Achievements"
          />
        </div>
      </Section>

      <Section
        title="Wishlist rank"
        badge={
          Number(value.wishlistRankFrom != null) +
          Number(value.wishlistRankTo != null)
        }
      >
        <p className="text-[11px] text-[#8f98a0] mb-2">
          Steam&apos;s Top Wishlist chart — mostly unreleased games, top ~4k
          tracked only · lower rank = more wishlisted
        </p>
        <MinMaxInputs
          from={value.wishlistRankFrom}
          to={value.wishlistRankTo}
          onFrom={(n) => onChange({ ...value, wishlistRankFrom: n })}
          onTo={(n) => onChange({ ...value, wishlistRankTo: n })}
          ariaLabel="Wishlist rank"
        />
      </Section>

      <Section title="Publisher" badge={value.publishers?.length ?? 0}>
        <CompanyFilterInput
          kind="publisher"
          selected={value.publishers}
          onChange={(next) => onChange({ ...value, publishers: next })}
        />
      </Section>

      <Section title="Developer" badge={value.developers?.length ?? 0}>
        <CompanyFilterInput
          kind="developer"
          selected={value.developers}
          onChange={(next) => onChange({ ...value, developers: next })}
        />
      </Section>

      <Section
        title="Show only content"
        badge={value.contentDescriptorIds?.length ?? 0}
      >
        <p className="text-[11px] text-[#8f98a0] mb-2">
          Show only games that have any of these content descriptors
        </p>
        <div className="space-y-0.5">
          {(Object.entries(CONTENT_DESCRIPTOR_LABELS) as [string, string][]).map(
            ([idStr, label]) => {
              const id = Number(idStr) as ContentDescriptorId;
              return (
                <CheckboxRow
                  key={id}
                  checked={(value.contentDescriptorIds ?? []).includes(id)}
                  label={label}
                  onClick={() =>
                    onChange({
                      ...value,
                      contentDescriptorIds: toggleInArray(
                        value.contentDescriptorIds,
                        id
                      ),
                    })
                  }
                />
              );
            }
          )}
        </div>
      </Section>

      {!hideReleaseDate && (
        <Section title="Release date" badge={Number(value.releaseFrom != null) + Number(value.releaseTo != null)}>
          {/* Stacked From/To — two native date inputs side-by-side overflow a
              narrow filter rail (the /search desktop sidebar is ~256px). */}
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2">
              <span className="w-9 shrink-0 text-[11px] text-[#8f98a0]">From</span>
              <input
                type="date"
                aria-label="Release date from"
                value={value.releaseFrom ?? ""}
                onChange={(e) => onChange({ ...value, releaseFrom: e.target.value || null })}
                className="h-8 min-w-0 flex-1 bg-[#316282]/30 px-2 text-[13px] text-white focus:outline-none focus:bg-[#316282]/50 transition-colors [color-scheme:dark]"
              />
            </label>
            <label className="flex items-center gap-2">
              <span className="w-9 shrink-0 text-[11px] text-[#8f98a0]">To</span>
              <input
                type="date"
                aria-label="Release date to"
                value={value.releaseTo ?? ""}
                onChange={(e) => onChange({ ...value, releaseTo: e.target.value || null })}
                className="h-8 min-w-0 flex-1 bg-[#316282]/30 px-2 text-[13px] text-white focus:outline-none focus:bg-[#316282]/50 transition-colors [color-scheme:dark]"
              />
            </label>
          </div>
        </Section>
      )}

      <Section title="Genres" badge={selectedCounts.genres}>
        <SearchInput value={genreQuery} onChange={setGenreQuery} />
        <div className="win-scrollbar max-h-52 overflow-y-auto overscroll-contain -mx-1 px-1 space-y-0.5">
          {filteredGenres.slice(0, MAX_FILTER_OPTIONS).map((g) => {
            const id = String(g.id);
            return (
              <CheckboxRow
                key={id}
                checked={(value.genres ?? []).includes(id)}
                label={g.name}
                onClick={() => onChange({ ...value, genres: toggleInArray(value.genres, id) })}
              />
            );
          })}
        </div>
      </Section>

      <Section title="Features" badge={selectedCounts.categories}>
        <SearchInput value={categoryQuery} onChange={setCategoryQuery} />
        <div className="win-scrollbar max-h-52 overflow-y-auto overscroll-contain -mx-1 px-1 space-y-0.5">
          {filteredCategories.slice(0, MAX_FILTER_OPTIONS).map((c) => {
            const id = typeof c.id === "number" ? c.id : Number(c.id);
            return (
              <CheckboxRow
                key={id}
                checked={(value.categories ?? []).includes(id)}
                label={c.name}
                icon={c.icon}
                onClick={() => onChange({ ...value, categories: toggleInArray(value.categories, id) })}
              />
            );
          })}
        </div>
      </Section>

      <Section title="Tags" badge={selectedCounts.tags}>
        <SearchInput value={tagQuery} onChange={setTagQuery} />
        <div className="win-scrollbar max-h-52 overflow-y-auto overscroll-contain -mx-1 px-1 space-y-0.5">
          {filteredTags.slice(0, MAX_FILTER_OPTIONS).map((t) => (
            <CheckboxRow
              key={t.id}
              checked={(value.tags ?? []).includes(t.id)}
              label={t.name}
              onClick={() => onChange({ ...value, tags: toggleInArray(value.tags, t.id) })}
            />
          ))}
        </div>
      </Section>

      <Section title="Languages" badge={selectedCounts.languages}>
        <SearchInput value={languageQuery} onChange={setLanguageQuery} />
        <div className="win-scrollbar max-h-52 overflow-y-auto overscroll-contain -mx-1 px-1 space-y-0.5">
          {filteredLanguages.slice(0, MAX_FILTER_OPTIONS).map((l) => (
            <CheckboxRow
              key={l.id}
              checked={(value.languages ?? []).includes(l.id)}
              label={l.name}
              onClick={() => onChange({ ...value, languages: toggleInArray(value.languages, l.id) })}
            />
          ))}
        </div>
      </Section>
      </div>
    </SectionOpenContext.Provider>
  );
}
